import { Input, Select, Spinner, Textarea } from '@cloudscape-design/components';
import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { createEventScenario } from '../../../../common/api/events';
import {
  Difficulty,
  EventScenarioCreated,
  ScenarioCopyRequest,
  ScenarioCreateRequest,
} from '../../../../common/types';
import { Modal } from '../../../../components/Modal';
import { copyScenario } from '../../../../common/api/scenarios';

export type CreateEventScenarioModalProps = {
  eventUuid: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess?: (data: EventScenarioCreated) => void;
};

export function CreateEventScenarioModal(props: CreateEventScenarioModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [prompt, setPrompt] = useState('');
  const [difficulty, setDifficulty] = useState<Difficulty>('medium');
  const [roomCount, setRoomCount] = useState(10);
  const [roomSize, setRoomSize] = useState(5);
  const [stageCount, setStageCount] = useState(3);

  const create = useMutation({
    mutationFn: (req: ScenarioCreateRequest) => createEventScenario(props.eventUuid, req),
    onSuccess: (data) => {
      props.setOpen(false);
      setName('');
      setDescription('');
      setDifficulty('medium');
      setRoomCount(10);
      setRoomSize(5);
      setStageCount(3);
      setPrompt('');
      props.onSuccess?.(data);
    },
  });
  const loading = create.isPending;

  const triggerCreate = useCallback(() => {
    if (loading) {
      return;
    }

    if (name.trim() === '') {
      return;
    }
    if (description.trim() === '') {
      return;
    }
    if (prompt.trim() === '') {
      return;
    }
    if (roomCount < 1) {
      return;
    }
    if (roomSize < 1) {
      return;
    }
    if (stageCount < 1) {
      return;
    }

    create.mutate({
      name: name.trim(),
      description: description.trim(),
      difficulty,
      roomCount,
      roomSize,
      stageCount,
      prompt: prompt.trim(),
    });
  }, [create, name, description, roomCount, roomSize, stageCount, loading, prompt]);

  return (
    <Modal isOpen={props.open} onClose={() => props.setOpen(false)} size="sm:w-1/2">
      <div className="px-4 py-4 space-y-2 text-white rounded-lg mx-auto my-auto grid grid-cols-1 gap-4 w-full">
        <div className="flex flex-col items-center text-center w-full">
          <form className="space-y-4 items-center text-left w-full">
            <div>
              <label className="block text-sm font-medium text-gray-200">Scenario Name</label>
              <Input
                className="w-full"
                name="name"
                disabled={loading}
                placeholder="Event name..."
                value={name}
                onChange={(e) => {
                  setName(e.detail.value);
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-200">Description</label>
              <Textarea
                name="description"
                disabled={loading}
                placeholder="Description..."
                value={description}
                onChange={(e) => {
                  setDescription(e.detail.value);
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-200">Detailed Prompt</label>
              <Textarea
                name="prompt"
                disabled={loading}
                placeholder="Prompt..."
                value={prompt}
                onChange={(e) => {
                  setPrompt(e.detail.value);
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-200">Difficulty</label>
              <Select
                name="difficulty"
                disabled={loading}
                options={[
                  { value: 'easy', label: 'Easy' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'hard', label: 'Hard' },
                ]}
                selectedOption={{
                  value: difficulty,
                  label: difficulty[0].toUpperCase() + difficulty.slice(1),
                }}
                onChange={(e) => {
                  setDifficulty(e.detail.selectedOption.value as Difficulty);
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-200">Room Count</label>
              <Input
                name="roomCount"
                disabled={loading}
                placeholder="Room count..."
                value={roomCount.toString()}
                onChange={(e) => {
                  setRoomCount(parseInt(e.detail.value, 10));
                }}
                type="number"
                inputMode="numeric"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-200">Room Size</label>
              <Input
                name="roomSize"
                disabled={loading}
                placeholder="Room size..."
                value={roomSize.toString()}
                onChange={(e) => {
                  setRoomSize(parseInt(e.detail.value, 10));
                }}
                type="number"
                inputMode="numeric"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-200">Stage Count</label>
              <Input
                name="stageCount"
                disabled={loading}
                placeholder="Stage count..."
                value={stageCount.toString()}
                onChange={(e) => {
                  setStageCount(parseInt(e.detail.value, 10));
                }}
                type="number"
                inputMode="numeric"
              />
            </div>
            <div className="text-center">
              <button
                className={`text-black font-semibold text-sm py-2 px-2 rounded ${
                  loading || !name.trim() ? 'bg-gray-400' : 'bg-white hover:bg-gray-200'
                }`}
                disabled={loading || !name.trim()}
                onClick={(e) => {
                  e.preventDefault();
                  triggerCreate();
                }}
                onKeyDown={(e) => {
                  e.preventDefault();
                  if (e.key === 'Enter') {
                    triggerCreate();
                  }
                }}
              >
                <div className="flex items-center justify-center w-full">
                  <span>Create</span>
                  {loading ? (
                    <div className="ml-2 w-4">
                      <Spinner />
                    </div>
                  ) : (
                    ''
                  )}
                </div>
              </button>
              {/* TODO display errors: {error && (
                <div className="mt-4">
                  <ErrorTurn error={error} />
                </div>
              )} */}
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}

export type CopyEventScenarioModalProps = {
  scenarioUuid: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess?: (data: EventScenarioCreated) => void;
};

export function CopyEventScenarioModal(props: CopyEventScenarioModalProps) {
  const [name, setName] = useState('');

  const create = useMutation({
    mutationFn: (req: ScenarioCopyRequest) => copyScenario(props.scenarioUuid, req),
    onSuccess: (data) => {
      props.setOpen(false);
      setName('');
      props.onSuccess?.(data);
    },
  });

  const loading = create.isPending;

  const triggerCreate = useCallback(() => {
    if (loading) {
      return;
    }

    if (name.trim() === '') {
      return;
    }

    create.mutate({ name: name.trim() });
  }, [create, name, loading]);

  return (
    <Modal isOpen={props.open} onClose={() => props.setOpen(false)} size="sm:w-1/2">
      <div className="px-4 py-4 space-y-2 text-white rounded-lg mx-auto my-auto grid grid-cols-1 gap-4 w-full">
        <div className="flex flex-col items-center text-center w-full">
          <h1>Copy Scenario</h1>
          <p>Copy the scenario to a new scenario with a new name.</p>

          <form className="space-y-4 items-center text-left w-full">
            <div>
              <label className="block text-sm font-medium text-gray-200">Scenario Name</label>
              <Input
                className="w-full"
                name="name"
                disabled={loading}
                placeholder="Event name..."
                value={name}
                onChange={(e) => {
                  setName(e.detail.value);
                }}
              />
            </div>
            <div className="text-center">
              <button
                className={`text-black font-semibold text-sm py-2 px-2 rounded ${
                  loading || !name.trim() ? 'bg-gray-400' : 'bg-white hover:bg-gray-200'
                }`}
                disabled={loading || !name.trim()}
                onClick={(e) => {
                  e.preventDefault();
                  triggerCreate();
                }}
              >
                <div className="flex items-center justify-center w-full">
                  <span>Create</span>
                  {loading ? (
                    <div className="ml-2 w-4">
                      <Spinner />
                    </div>
                  ) : (
                    ''
                  )}
                </div>
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
